import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowRight, Sun, Moon, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, SERVICES, SORTED_AREAS } from "@/lib/constants";

export default function SiteFooter() {
  const [night, setNight] = useState(true);
  const [spark, setSpark] = useState(null);

  return (
    <footer className="relative overflow-hidden text-white" data-testid="site-footer">
      {/* Same walkway — day & night cross-fade */}
      <img src="/archive/night-lights.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-[1200ms]" style={{ opacity: night ? 1 : 0 }} />
      <img src="/archive/day-lights.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-[1200ms]" style={{ opacity: night ? 0 : 1 }} />
      {/* readability + mood overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/80 to-black" />
      <div className="absolute inset-0 bg-orange-950/25 transition-opacity duration-[1200ms]" style={{ opacity: night ? 1 : 0 }} />
      <div className="absolute inset-0 grain-texture opacity-50" />

      <div className="relative">
        {/* CTA band */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 text-center border-b border-white/10">
          {/* Day / Night toggle */}
          <div className="inline-flex items-center gap-1 border border-white/20 rounded-full p-1 bg-black/40 backdrop-blur mb-7" data-testid="footer-daynight-toggle" role="group" aria-label="Toggle day or night view">
            <button onClick={() => setNight(false)} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${!night ? "bg-primary text-white" : "text-white/60 hover:text-white"}`} data-testid="footer-toggle-day" aria-pressed={!night}>
              <Sun className="h-4 w-4" /> Day
            </button>
            <button onClick={() => setNight(true)} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${night ? "bg-primary text-white" : "text-white/60 hover:text-white"}`} data-testid="footer-toggle-night" aria-pressed={night}>
              <Moon className="h-4 w-4" /> Night
            </button>
          </div>

          <div className="text-primary font-bold text-sm tracking-[0.2em]">SAME CONCRETE, {night ? "AFTER DARK" : "IN DAYLIGHT"}</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 max-w-3xl mx-auto leading-tight">
            Concrete That Shines — <span className="text-primary">Day or Night</span>
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto text-lg">
            The same stamped walkway, morning to midnight. We build outdoor spaces you'll love around the clock — now serving {SORTED_AREAS.length}+ communities across southeast Wisconsin.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="h-12 px-8 bg-primary hover:bg-primary/90 rounded-sm font-bold hover:-translate-y-1 transition-transform" data-testid="footer-quote-btn">
              <Link to="/contact">Get Your Free Quote <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-8 rounded-sm font-bold bg-transparent border-white/30 text-white hover:bg-white hover:text-secondary">
              <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}</a>
            </Button>
          </div>
        </div>

        {/* Columns */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="/archive/logo.jpg" alt="CG Concrete LLC" className="h-14 w-14 object-contain rounded-sm bg-white p-0.5" />
              <div className="font-heading font-extrabold text-lg">CG CONCRETE LLC</div>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">
              Locally owned concrete contractor serving Watertown, WI and surrounding communities since {COMPANY.founded}. Licensed &amp; fully insured.
            </p>
          </div>

          <div>
            <h4 className="font-heading font-bold mb-4 text-white">Services</h4>
            <ul className="space-y-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm text-white/60 hover:text-primary transition-colors" data-testid={`footer-service-${s.slug}`}>{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold mb-4 text-white">Contact</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2"><Phone className="h-4 w-4 text-primary mt-0.5" /><a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-primary">{COMPANY.phone}</a></li>
              <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-primary mt-0.5" /><a href={`mailto:${COMPANY.email}`} className="hover:text-primary break-all">{COMPANY.email}</a></li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-primary mt-0.5" /><span>{COMPANY.address}, {COMPANY.city}, {COMPANY.state}</span></li>
              <li className="flex items-start gap-2"><Clock className="h-4 w-4 text-primary mt-0.5" /><span>{COMPANY.hours}</span></li>
            </ul>
          </div>

          <div className="md:col-span-2 lg:col-span-1">
            <h4 className="font-heading font-bold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-2">
              {[["/gallery", "Project Gallery"], ["/about", "About Us"], ["/service-areas", "Service Areas"], ["/careers", "Careers"], ["/contact", "Free Quote"], ["/login", "Client Portal"]].map(([to, label]) => (
                <li key={to}><Link to={to} className="text-sm text-white/60 hover:text-primary transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Service Areas */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h4 className="font-heading font-bold text-white flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Proudly Serving Southeast Wisconsin</h4>
            <Link to="/service-areas" className="text-sm font-bold text-primary hover:gap-2 flex items-center gap-1 shrink-0">All areas <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {SORTED_AREAS.map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-xs sm:text-sm font-semibold px-3 py-1.5 border border-white/15 bg-white/5 hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors" data-testid={`footer-area-${a.slug}`}>
                {a.city}
              </Link>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 flex flex-col items-center gap-5">
            {/* Artsy designer signature — click for a spark burst */}
            <button
              type="button"
              onClick={() => {
                const colors = ["hsl(var(--primary))", "#fcd34d", "#fff7ed", "#fb923c"];
                const particles = Array.from({ length: 16 }).map(() => {
                  const angle = Math.random() * Math.PI * 2;
                  const dist = 34 + Math.random() * 46;
                  return { dx: Math.cos(angle) * dist, dy: Math.sin(angle) * dist, color: colors[Math.floor(Math.random() * colors.length)] };
                });
                setSpark({ id: Date.now(), particles });
                setTimeout(() => setSpark(null), 750);
              }}
              className="credit-link group relative inline-flex items-center gap-2.5 select-none focus:outline-none"
              data-testid="designer-credit"
              aria-label="Designed by Joshua Israel Rock"
            >
              <span className="text-[11px] uppercase tracking-[0.35em] text-white/40 group-hover:text-white/60 transition-colors">Crafted&nbsp;by</span>
              <Zap className="credit-bolt h-4 w-4 text-primary group-hover:animate-pulse" />
              <span className={`credit-name font-heading font-extrabold text-lg sm:text-xl tracking-tight leading-none ${spark ? "credit-pop" : ""}`}>
                Joshua Israel Rock
              </span>
              <sup className="text-primary font-bold text-[10px] -ml-0.5">™</sup>

              {spark && (
                <span key={spark.id} className="pointer-events-none absolute left-1/2 top-1/2" aria-hidden="true">
                  {spark.particles.map((p, i) => (
                    <span key={i} className="spark" style={{ "--dx": `${p.dx}px`, "--dy": `${p.dy}px`, background: p.color }} />
                  ))}
                </span>
              )}
            </button>

            <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40 border-t border-white/5 pt-5">
              <span>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
              <div className="flex gap-4">
                <Link to="/login" className="hover:text-primary">Client Portal</Link>
                <Link to="/login" className="hover:text-primary">Admin</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
